/**
 * The single campaign presentation-state adapter.
 *
 * `app/models/campaign.rb` only has four status values:
 *
 *   enum campaign_status: { active: 0, completed: 1, processing: 2, draft: 3 }
 *
 * The UI may only write the publish transition (`draft` -> `active`, via the
 * builder). `Paused` and `Scheduled` are *derived* from `enabled` and
 * `scheduled_at` — they are not, and must not become, backend enum values.
 *
 * Resolution order, most authoritative fact first:
 *   1. `processing`  — the backend says a run is in flight.
 *   2. `completed`   — the backend says the run finished.
 *   3. `draft`       — unpublished; `enabled`/`scheduled_at` say nothing yet.
 *   4. `paused`      — `enabled === false` (configuration, not run state).
 *   5. `scheduled`   — a one-off campaign whose `scheduled_at` is still ahead.
 *   6. `active`      — everything else.
 *
 * `scheduled_at` is serialized as epoch **seconds** (`.to_i` in
 * `app/views/api/v1/models/_campaign.json.jbuilder`).
 */

export const CAMPAIGN_STATUS = {
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  DRAFT: 'draft',
  PAUSED: 'paused',
  SCHEDULED: 'scheduled',
  ACTIVE: 'active',
};

const BACKEND_STATUS_PROCESSING = 'processing';
const BACKEND_STATUS_COMPLETED = 'completed';
const BACKEND_STATUS_DRAFT = 'draft';
const CAMPAIGN_TYPE_ONE_OFF = 'one_off';

/**
 * Presentation states that `components-next/status-chip/StatusChip.vue` can
 * express faithfully. `active` and `completed` have no member in its
 * `STATUS_OPTIONS` vocabulary, so they are not listed here.
 */
export const STATUS_CHIP_SUPPORTED_STATUSES = [
  CAMPAIGN_STATUS.PROCESSING,
  CAMPAIGN_STATUS.DRAFT,
  CAMPAIGN_STATUS.PAUSED,
  CAMPAIGN_STATUS.SCHEDULED,
];

/**
 * @param {object} campaign
 * @param {string} [campaign.campaignStatus] Backend `campaign_status` enum value.
 * @param {string} [campaign.campaignType] Backend `campaign_type` enum value.
 * @param {boolean} [campaign.enabled]
 * @param {number} [campaign.scheduledAt] Epoch seconds; one-off campaigns only.
 * @returns {string} One of `CAMPAIGN_STATUS`.
 */
export const deriveCampaignStatus = ({
  campaignStatus,
  campaignType,
  enabled,
  scheduledAt,
}) => {
  if (campaignStatus === BACKEND_STATUS_PROCESSING) {
    return CAMPAIGN_STATUS.PROCESSING;
  }

  if (campaignStatus === BACKEND_STATUS_COMPLETED) {
    return CAMPAIGN_STATUS.COMPLETED;
  }

  // A draft is unpublished configuration — `enabled`/`scheduled_at` say
  // nothing until the builder publishes it.
  if (campaignStatus === BACKEND_STATUS_DRAFT) {
    return CAMPAIGN_STATUS.DRAFT;
  }

  // `enabled` is only honoured for ongoing campaigns: the widget controller
  // filters on `enabled: true`, while the one-off path (TriggerScheduledItemsJob
  // and Campaign#trigger!) never reads it and will still send on schedule.
  // Showing Paused for a one-off would present a state the backend ignores.
  if (enabled === false && campaignType !== CAMPAIGN_TYPE_ONE_OFF) {
    return CAMPAIGN_STATUS.PAUSED;
  }

  if (
    campaignType === CAMPAIGN_TYPE_ONE_OFF &&
    scheduledAt &&
    scheduledAt * 1000 > Date.now()
  ) {
    return CAMPAIGN_STATUS.SCHEDULED;
  }

  return CAMPAIGN_STATUS.ACTIVE;
};
